@file:Suppress("TooGenericExceptionCaught")

package it.unibo.tuprolog.solve.libs.io

import it.unibo.tuprolog.solve.channel.InputChannel
import it.unibo.tuprolog.solve.channel.OutputChannel
import it.unibo.tuprolog.solve.libs.io.channel.SinkOutputChannel
import it.unibo.tuprolog.solve.libs.io.channel.SourceInputChannel
import it.unibo.tuprolog.solve.libs.io.exceptions.IOException
import okio.FileSystem
import okio.NodeJsFileSystem
import okio.Path
import okio.Path.Companion.toPath
import okio.buffer

internal actual val platformFileSystem: FileSystem = NodeJsFileSystem

// Node's own file:-URL <-> native-path conversion: correctly handles Windows drive letters,
// backslash-vs-forward-slash, and percent-decoding, so `toLocalPath` doesn't have to. 'url' is a Node builtin,
// not an npm package: called (see toLocalPath's only callers, gated on isNode) only under Node, but a literal
// `require('url')` is still enough for webpack's static analysis to try to bundle it for a browser build too,
// where it doesn't exist - "Module not found: Error: Can't resolve 'url'". Routing the call through a runtime
// `eval('require')` (the standard way to keep an optional Node-only require out of a bundler's static analysis)
// avoids that: it's still `require('url')` once actually executed under Node, just invisible to webpack.
private val FILE_URL_TO_PATH: dynamic by lazy { js("eval('require')('url').fileURLToPath") }

actual fun parseUrl(string: String): Url = JsUrl(string)

actual fun fileUrl(path: String): Url = JsUrl(protocol = "file", path = path)

actual fun remoteUrl(
    protocol: String,
    host: String,
    port: Int?,
    path: String,
    query: String?,
): Url = JsUrl(protocol, host, port, path, query)

/** Delegates to Node's own `fileURLToPath`, falling back to the [Url.path] component as-is when that throws. */
internal actual fun Url.toLocalPath(): Path =
    try {
        (FILE_URL_TO_PATH(toString()) as String).toPath()
    } catch (_: Throwable) {
        // fileURLToPath (on Windows) requires either a UNC host or a genuine `<letter>:` drive
        // prefix, and throws for a plain Unix-shaped absolute path (e.g. `/path/to/x.pl`) - even
        // though such a path is exactly what Node's own fs calls happily resolve relative to the
        // current drive. Fall back to the URL's plain (already `/`-rooted) path component.
        path.toPath()
    }

private fun <T> wrappingIOException(action: () -> T): T =
    try {
        action()
    } catch (e: okio.IOException) {
        throw IOException(e.message, e)
    }

/**
 * Under Node, streams a local file lazily off disk. Everywhere else (browser-local or any remote resource),
 * eagerly reads the whole resource via [Url.readAsText] into an in-memory [InputChannel] instead, since Okio has
 * no synchronous file system for browsers and this module has no HTTP streaming client (see [fetch]).
 */
actual fun Url.openInputChannel(): InputChannel<String> =
    if (isFile && isNode) {
        wrappingIOException { SourceInputChannel(LocalFileSystem.source(toLocalPath()).buffer()) }
    } else {
        InputChannel.of(readAsText())
    }

/**
 * Only supported for local files under Node: browsers have no writable local file system, and this module never
 * supports writing to remote resources (on any platform).
 * @throws IOException unless [Url.isFile] && [isNode].
 */
actual fun Url.openOutputChannel(append: Boolean): OutputChannel<String> {
    if (!isFile || !isNode) {
        throw IOException("Writing not supported for ${toString()}")
    }
    val path = toLocalPath()
    return wrappingIOException {
        val sink = if (append) LocalFileSystem.appendingSink(path) else LocalFileSystem.sink(path)
        SinkOutputChannel(sink.buffer())
    }
}
